import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Badge } from '../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import { Modal } from '../../components/ui/Modal';
import { 
  Key, 
  Plus, 
  Edit, 
  Trash2, 
  Play, 
  Pause, 
  Settings,
  Eye,
  EyeOff,
  Copy,
  RefreshCw,
  Shield,
  BarChart3,
  Filter,
  Search,
  Save,
  RotateCcw,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  Zap,
  Target,
  ArrowRight,
  ArrowDown,
  ArrowUp,
  Activity,
  Database,
  Globe,
  Lock,
  Unlock,
  Calendar,
  FileText,
  Download,
  Upload,
  Info,
  XCircle,
  Link
} from 'lucide-react';
import { useDataContext } from '../../contexts/DataContext';

// Mock API Keys Data
const mockAPIKeysData = [
  {
    id: 1,
    name: 'HelpDesk Mobile App',
    description: 'API key for mobile application integration',
    application: 'Mobile App',
    keyValue: 'hd_mobile_ak_1234567890abcdef1234567890abcdef',
    publicKey: 'hd_mobile_pk_1234567890abcdef',
    status: 'active',
    permissions: ['read', 'write'],
    rateLimit: 1000,
    rateLimitPeriod: 'hour',
    usageCount: 15420,
    lastUsed: '2025-01-15T10:30:00Z',
    createdAt: '2024-12-01T00:00:00Z',
    expiresAt: '2025-12-01T00:00:00Z',
    lastRotated: '2024-12-01T00:00:00Z',
    rotationCount: 0,
    ipWhitelist: ['192.168.1.0/24', '10.0.0.0/8'],
    environment: 'production'
  },
  {
    id: 2,
    name: 'Third-party Integration',
    description: 'API key for external system integration',
    application: 'External System',
    keyValue: 'hd_external_ak_abcdef1234567890abcdef1234567890',
    publicKey: 'hd_external_pk_abcdef1234567890',
    status: 'active',
    permissions: ['read'],
    rateLimit: 500,
    rateLimitPeriod: 'hour',
    usageCount: 8920,
    lastUsed: '2025-01-15T09:45:00Z',
    createdAt: '2024-11-15T00:00:00Z',
    expiresAt: '2025-11-15T00:00:00Z',
    lastRotated: '2024-11-15T00:00:00Z',
    rotationCount: 1,
    ipWhitelist: ['203.0.113.0/24'],
    environment: 'production'
  },
  {
    id: 3,
    name: 'Development Testing',
    description: 'API key for development and testing purposes',
    application: 'Development',
    keyValue: 'hd_dev_ak_9876543210fedcba9876543210fedcba',
    publicKey: 'hd_dev_pk_9876543210fedcba',
    status: 'inactive',
    permissions: ['read', 'write', 'admin'],
    rateLimit: 10000,
    rateLimitPeriod: 'day',
    usageCount: 2340,
    lastUsed: '2025-01-10T14:30:00Z',
    createdAt: '2024-09-15T00:00:00Z',
    expiresAt: '2025-09-15T00:00:00Z',
    lastRotated: '2024-09-15T00:00:00Z',
    rotationCount: 0,
    ipWhitelist: [],
    environment: 'development'
  },
  {
    id: 4,
    name: 'Webhook Service',
    description: 'API key for webhook authentication',
    application: 'Webhook Service',
    keyValue: 'hd_webhook_ak_fedcba0987654321fedcba0987654321',
    publicKey: 'hd_webhook_pk_fedcba0987654321',
    status: 'active',
    permissions: ['write'],
    rateLimit: 2000,
    rateLimitPeriod: 'hour',
    usageCount: 5670,
    lastUsed: '2025-01-15T08:20:00Z',
    createdAt: '2024-08-01T00:00:00Z',
    expiresAt: '2025-08-01T00:00:00Z',
    lastRotated: '2024-08-01T00:00:00Z',
    rotationCount: 2,
    ipWhitelist: ['198.51.100.0/24'],
    environment: 'production'
  },
  {
    id: 5,
    name: 'Analytics Dashboard',
    description: 'API key for analytics dashboard data access',
    application: 'Analytics Dashboard',
    keyValue: 'hd_analytics_ak_13579ace2468bdf013579ace2468bdf0',
    publicKey: 'hd_analytics_pk_13579ace2468bdf0',
    status: 'expired',
    permissions: ['read'],
    rateLimit: 100,
    rateLimitPeriod: 'minute',
    usageCount: 1230,
    lastUsed: '2025-01-05T16:45:00Z',
    createdAt: '2024-07-01T00:00:00Z',
    expiresAt: '2025-01-01T00:00:00Z',
    lastRotated: '2024-07-01T00:00:00Z',
    rotationCount: 0,
    ipWhitelist: ['172.16.0.0/16'],
    environment: 'production'
  }
];

const APIKeyManagement = () => {
  const navigate = useNavigate();
  const { getMockData, updateMockData } = useDataContext();
  const [apiKeys, setApiKeys] = useState(mockAPIKeysData);
  const [applications, setApplications] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showSecretKeys, setShowSecretKeys] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMetricModal, setShowMetricModal] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    application: '',
    permissions: [],
    rateLimit: 1000,
    rateLimitPeriod: 'hour',
    ipWhitelist: [],
    environment: 'production'
  });

  useEffect(() => {
    loadAPIKeyData();
  }, []);

  const loadAPIKeyData = async () => {
    try {
      const data = getMockData('apiKeys');
      const applicationsData = getMockData('applications');
      const permissionsData = getMockData('permissions');
      
      // Only update state if data exists, otherwise keep initial mock data
      if (data?.apiKeys && data.apiKeys.length > 0) {
        setApiKeys(data.apiKeys);
      }
      if (applicationsData?.applications) {
        setApplications(applicationsData.applications);
      }
      if (permissionsData?.permissions) {
        setPermissions(permissionsData.permissions);
      }
    } catch (error) {
      console.error('Failed to load API key data:', error);
    }
  };

  const handleCreateKey = () => {
    setFormData({
      name: '',
      description: '',
      application: '',
      permissions: [],
      rateLimit: 1000,
      rateLimitPeriod: 'hour',
      ipWhitelist: [],
      environment: 'production'
    });
    setShowCreateModal(true);
  };

  const handleSaveNewKey = () => {
    const newKey = {
      id: apiKeys.length + 1,
      ...formData,
      keyValue: `hd_${formData.environment}_ak_${generateAPIKey()}`,
      publicKey: `hd_${formData.environment}_pk_${generateAPIKey().substring(0, 16)}`,
      status: 'active',
      usageCount: 0,
      lastUsed: null,
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
      lastRotated: new Date().toISOString(),
      rotationCount: 0
    };
    
    const updatedKeys = [...apiKeys, newKey];
    setApiKeys(updatedKeys);
    updateMockData('apiKeys', { apiKeys: updatedKeys });
    setShowCreateModal(false);
    alert(`API Key created successfully!\n\nKey: ${newKey.keyValue}\n\nPlease save this key securely. You won't be able to see it again.`);
  };

  const handleEditKey = (key) => {
    setEditingKey(key);
    setFormData({
      name: key.name,
      description: key.description,
      application: key.application,
      permissions: key.permissions,
      rateLimit: key.rateLimit,
      rateLimitPeriod: key.rateLimitPeriod,
      ipWhitelist: key.ipWhitelist,
      environment: key.environment
    });
    setShowEditModal(true);
  };

  const handleSaveEditKey = () => {
    const updatedKeys = apiKeys.map(key => 
      key.id === editingKey.id ? { ...key, ...formData } : key
    );
    setApiKeys(updatedKeys);
    updateMockData('apiKeys', { apiKeys: updatedKeys });
    setShowEditModal(false);
    setEditingKey(null);
    alert('API Key updated successfully!');
  };

  const handleDeleteKey = (keyId) => {
    if (window.confirm('Are you sure you want to delete this API key?')) {
      const updatedKeys = apiKeys.filter(key => key.id !== keyId);
      setApiKeys(updatedKeys);
      updateMockData('apiKeys', { apiKeys: updatedKeys });
    }
  };

  const handleToggleKey = (keyId) => {
    const updatedKeys = apiKeys.map(key => {
      if (key.id === keyId) {
        const newStatus = key.status === 'active' ? 'inactive' : 'active';
        return { 
          ...key, 
          status: newStatus,
          isActive: newStatus === 'active'
        };
      }
      return key;
    });
    
    setApiKeys(updatedKeys);
    updateMockData('apiKeys', { apiKeys: updatedKeys });
    
    const updatedKey = updatedKeys.find(k => k.id === keyId);
    alert(`API Key "${updatedKey.name}" has been ${updatedKey.status === 'active' ? 'activated' : 'deactivated'} successfully!`);
  };

  const handleRegenerateKey = (keyId) => {
    if (window.confirm('Are you sure you want to regenerate this API key? This will invalidate the current key.')) {
      const updatedKeys = apiKeys.map(key => 
        key.id === keyId ? { 
          ...key, 
          keyValue: generateAPIKey(),
          lastRotated: new Date().toISOString(),
          rotationCount: (key.rotationCount || 0) + 1
        } : key
      );
      setApiKeys(updatedKeys);
      updateMockData('apiKeys', { apiKeys: updatedKeys });
    }
  };

  const generateAPIKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleCopyKey = (keyValue) => {
    navigator.clipboard.writeText(keyValue);
    alert('API key copied to clipboard!');
  };

  const toggleSecretVisibility = (keyId) => {
    setShowSecretKeys(prev => ({
      ...prev,
      [keyId]: !prev[keyId]
    }));
  };

  const handleUsageAnalytics = () => {
    navigate('/integration/analytics');
  };

  const handleExportKeys = () => {
    const keysData = apiKeys.map(key => ({
      name: key.name,
      application: key.application,
      status: key.status,
      environment: key.environment,
      createdAt: key.createdAt,
      usageCount: key.usageCount,
      permissions: key.permissions.join(', ')
    }));
    
    //console.log('Exporting API Keys:', keysData);
    alert(`Exporting ${keysData.length} API keys to CSV...\n\nDownload will start automatically.`);
  };

  const handleSecuritySettings = () => {
    navigate('/integration/settings');
  };

  const handleMetricClick = (metric) => {
    setSelectedMetric(metric);
    setShowMetricModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'gray';
      case 'expired': return 'red';
      case 'revoked': return 'red';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return CheckCircle;
      case 'inactive': return Pause;
      case 'expired': return AlertTriangle;
      case 'revoked': return AlertTriangle;
      default: return AlertTriangle;
    }
  };

  const getPermissionColor = (permission) => {
    switch (permission) {
      case 'read': return 'blue';
      case 'write': return 'yellow';
      case 'admin': return 'red';
      case 'full': return 'purple';
      default: return 'gray';
    }
  };

  const filteredKeys = apiKeys.filter(key => {
    const matchesSearch = key.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         key.application.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         key.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || key.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const apiKeyMetrics = [
    {
      title: 'Total API Keys',
      value: mockAPIKeysData.length,
      icon: Key,
      bgColor: '#006600',
      textColor: 'white',
      trend: '+2',
      trendDirection: 'up',
      details: {
        total: 5,
        active: 3,
        inactive: 1,
        expired: 1,
        description: 'Total number of API keys across all environments'
      }
    },
    {
      title: 'Active Keys',
      value: mockAPIKeysData.filter(k => k.status === 'active').length,
      icon: CheckCircle,
      bgColor: '#BB0000',
      textColor: 'white',
      trend: '+1',
      trendDirection: 'up',
      details: {
        production: 3,
        staging: 0,
        development: 0,
        totalRequests: 30750,
        description: 'Currently active API keys in use'
      }
    },
    {
      title: 'Total Usage Today',
      value: 45230,
      icon: Activity,
      bgColor: '#006600',
      textColor: 'white',
      trend: '+12%',
      trendDirection: 'up',
      details: {
        successful: 44120,
        failed: 1110,
        avgResponseTime: '145ms',
        description: 'Total API requests processed today'
      }
    },
    {
      title: 'Rate Limit Hits',
      value: 23,
      icon: AlertTriangle,
      bgColor: '#BB0000',
      textColor: 'white',
      trend: '-5',
      trendDirection: 'down',
      details: {
        today: 23,
        yesterday: 28,
        thisWeek: 156,
        description: 'Number of requests that hit rate limits'
      }
    }
  ];

  return (
    <div className="api-key-management p-5 bg-[#F8FCFB] min-h-screen">
      {/* Header */}
      <div className="mb-5 bg-white rounded-2xl shadow-sm p-5 border border-[#E5EFF3]">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-[#0B2530]">API Key Management</h1>
            <p className="text-[#4A5B63] mt-1">Manage API keys with proper scoping and permissions</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button 
              onClick={handleUsageAnalytics}
              className="px-5 py-3 font-medium shadow-md transition-all duration-300 hover:scale-105 hover:shadow-lg"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Usage Analytics
            </Button>
            <Button 
              onClick={handleCreateKey}
              className="px-5 py-3 font-medium shadow-md transition-all duration-300 hover:scale-105 hover:shadow-lg"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create API Key
            </Button>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        {apiKeyMetrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <Card 
              key={index} 
              className="hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-105"
              onClick={() => handleMetricClick(metric)}
              style={{ 
                background: `linear-gradient(135deg, ${metric.bgColor} 0%, ${metric.bgColor}dd 100%)`,
                border: 'none'
              }}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium" style={{ color: metric.textColor }}>
                  {metric.title}
                </CardTitle>
                <Icon className="h-5 w-5" style={{ color: metric.textColor }} />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" style={{ color: metric.textColor }}>
                  {metric.value.toLocaleString ? metric.value.toLocaleString() : metric.value}
                </div>
                <div className="flex items-center text-sm mt-2" style={{ color: metric.textColor, opacity: 0.9 }}>
                  {metric.trendDirection === 'up' ? (
                    <ArrowUp className="h-4 w-4 mr-1" />
                  ) : (
                    <ArrowDown className="h-4 w-4 mr-1" />
                  )}
                  <span>{metric.trend} from yesterday</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters and Search */}
      <Card className="mb-6 shadow-md border-[#E5EFF3]">
        <CardHeader className="bg-gradient-to-r from-[#F8FCFB] to-white border-b border-[#E5EFF3]">
          <CardTitle className="flex items-center text-[#0B2530]">
            <Filter className="h-5 w-5 mr-2" style={{ color: '#006600' }} />
            Filter API Keys
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search 
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 z-10" 
                  style={{ color: '#8A9AA2' }}
                />
                <Input
                  placeholder="Search API keys by name, application, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 pr-4 py-3 text-base border-2 focus:border-[#006600] focus:ring-2 focus:ring-[#006600]/20 rounded-xl transition-all shadow-sm"
                  style={{
                    borderColor: searchTerm ? '#006600' : '#E2E8F0',
                    backgroundColor: searchTerm ? '#E8FCF7' : 'white'
                  }}
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger 
                className="w-full md:w-56 h-12 border-2 rounded-xl shadow-sm transition-all hover:border-[#006600]"
                style={{
                  borderColor: filterStatus !== 'all' ? '#006600' : '#E2E8F0'
                }}
              >
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Keys</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="inactive">Inactive Only</SelectItem>
                <SelectItem value="expired">Expired Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* API Keys List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Key className="h-5 w-5 mr-2" />
            API Keys ({apiKeys.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {apiKeys.map((key) => {
              const StatusIcon = getStatusIcon(key.status);
              const isSecretVisible = showSecretKeys[key.id];
              return (
                <div key={key.id} className="p-6 border rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold">{key.name}</h3>
                        <Badge variant={key.status === 'active' ? 'default' : 'secondary'}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {key.status}
                        </Badge>
                        <Badge variant="outline">{key.application}</Badge>
                        <Badge variant="outline">{key.environment}</Badge>
                      </div>
                      <p className="text-gray-600 mb-3">{key.description}</p>
                      
                      {/* API Key Value */}
                      <div className="mb-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">API Key:</h4>
                        <div className="flex items-center space-x-2">
                          <div className="flex-1 p-2 bg-gray-100 rounded font-mono text-sm">
                            {isSecretVisible ? key.keyValue : '••••••••••••••••••••••••••••••••'}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleSecretVisibility(key.id)}
                          >
                            {isSecretVisible ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCopyKey(key.keyValue)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>

                      {/* Permissions */}
                      <div className="mb-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Permissions:</h4>
                        <div className="flex flex-wrap gap-2">
                          {key.permissions.map((permission, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {permission}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Rate Limiting */}
                      <div className="mb-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Rate Limiting:</h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span>{key.rateLimit} requests per {key.rateLimitPeriod}</span>
                          <Badge variant="outline" className="text-xs">
                            {key.usageCount} total uses
                          </Badge>
                        </div>
                      </div>

                      {/* IP Whitelist */}
                      {key.ipWhitelist.length > 0 && (
                        <div className="mb-3">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">IP Whitelist:</h4>
                          <div className="flex flex-wrap gap-2">
                            {key.ipWhitelist.map((ip, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {ip}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Metadata */}
                      <div className="flex items-center space-x-6 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          <span>Created: {new Date(key.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          <span>Last Used: {new Date(key.lastUsed).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center">
                          <RefreshCw className="h-4 w-4 mr-1" />
                          <span>Rotated: {key.rotationCount} times</span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRegenerateKey(key.id)}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Regenerate
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditKey(key)}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggleKey(key.id)}
                      >
                        {key.status === 'active' ? (
                          <>
                            <Pause className="h-3 w-3 mr-1" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <Play className="h-3 w-3 mr-1" />
                            Activate
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteKey(key.id)}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Zap className="h-5 w-5 mr-2" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Button onClick={handleCreateKey} className="h-20 flex flex-col items-center justify-center space-y-2">
              <Plus className="h-6 w-6" />
              <span>Create API Key</span>
            </Button>
            <Button variant="outline" onClick={handleUsageAnalytics} className="h-20 flex flex-col items-center justify-center space-y-2">
              <BarChart3 className="h-6 w-6" />
              <span>Usage Analytics</span>
            </Button>
            <Button variant="outline" onClick={handleSecuritySettings} className="h-20 flex flex-col items-center justify-center space-y-2">
              <Shield className="h-6 w-6" />
              <span>Security Settings</span>
            </Button>
            <Button variant="outline" onClick={handleExportKeys} className="h-20 flex flex-col items-center justify-center space-y-2">
              <Download className="h-6 w-6" />
              <span>Export Keys</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Create API Key Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <div className="max-w-6xl -m-6 -mt-6">
          {/* Modal Header - Fixed at top */}
          <div className="bg-gradient-to-r from-[#307A91] to-[#348299] px-8 py-4 relative sticky top-0 z-10">
            <button
              onClick={() => setShowCreateModal(false)}
              className="absolute top-3 right-4 text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-all duration-200"
              aria-label="Close"
            >
              <XCircle className="h-5 w-5" />
            </button>
            <div className="flex items-center space-x-3 pr-10">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center shadow-lg">
                <Key className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Create New API Key</h2>
                <p className="text-white/90 text-xs">Configure your API key with specific permissions and security settings</p>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="p-8 space-y-6 max-h-[65vh] overflow-y-auto bg-gray-50">
            {/* Basic Information Section */}
            <div className="bg-white p-5 shadow-md">
              <div className="flex items-center gap-3 mb-5 pb-3 border-b-2 border-gray-100">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#307A91] to-[#348299] flex items-center justify-center shadow-md">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Basic Information</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Provide essential details about your API key</p>
                </div>
              </div>

              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                      Key Name <span className="text-[#FE7F50]">*</span>
                    </Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., Production API Key"
                      className="h-11 border-gray-300 focus:border-[#307A91] focus:ring-[#307A91]"
                    />
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Info className="h-3 w-3" />
                      A unique identifier for this API key
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                      Application <span className="text-[#FE7F50]">*</span>
                    </Label>
                    <Input
                      value={formData.application}
                      onChange={(e) => setFormData({...formData, application: e.target.value})}
                      placeholder="e.g., Mobile App, Web Dashboard"
                      className="h-11 border-gray-300 focus:border-[#307A91] focus:ring-[#307A91]"
                    />
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Info className="h-3 w-3" />
                      The application that will use this key
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-700">Description</Label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe the purpose and functionality of this API key..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#307A91] focus:ring-2 focus:ring-[#307A91]/20 resize-none"
                    rows="3"
                  />
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    Optional description for documentation purposes
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                      Environment <span className="text-[#FE7F50]">*</span>
                    </Label>
                    <Select value={formData.environment} onValueChange={(value) => setFormData({...formData, environment: value})}>
                      <SelectTrigger className="h-11 border-gray-300">
                        <SelectValue placeholder="Select environment" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="production">
                          <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4 text-[#307A91]" />
                            <span>Production</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="staging">
                          <div className="flex items-center gap-2">
                            <Activity className="h-4 w-4 text-[#348299]" />
                            <span>Staging</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="development">
                          <div className="flex items-center gap-2">
                            <Database className="h-4 w-4 text-[#FF7F4C]" />
                            <span>Development</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-gray-700">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                      <SelectTrigger className="h-11 border-gray-300">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-[#307A91]" />
                            <span>Active</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="inactive">
                          <div className="flex items-center gap-2">
                            <Pause className="h-4 w-4 text-gray-500" />
                            <span>Inactive</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Rate Limiting Section */}
            <div className="bg-white p-5 shadow-md">
              <div className="flex items-center gap-3 mb-5 pb-3 border-b-2 border-gray-100">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FE7F50] to-[#FF7F4C] flex items-center justify-center shadow-md">
                  <Zap className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Rate Limiting</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Control API request frequency and prevent abuse</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                    Rate Limit <span className="text-[#FE7F50]">*</span>
                  </Label>
                  <Input
                    type="number"
                    value={formData.rateLimit}
                    onChange={(e) => setFormData({...formData, rateLimit: parseInt(e.target.value)})}
                    placeholder="1000"
                    className="h-11 border-gray-300 focus:border-[#FE7F50] focus:ring-[#FE7F50]"
                    min="1"
                  />
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Target className="h-3 w-3" />
                    Maximum number of requests allowed
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                    Period <span className="text-[#FE7F50]">*</span>
                  </Label>
                  <Select value={formData.rateLimitPeriod} onValueChange={(value) => setFormData({...formData, rateLimitPeriod: value})}>
                    <SelectTrigger className="h-11 border-gray-300">
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minute">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-[#FE7F50]" />
                          <span>Per Minute</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="hour">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-[#FF7F4C]" />
                          <span>Per Hour</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="day">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-[#348299]" />
                          <span>Per Day</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Time window for the rate limit
                  </p>
                </div>
              </div>
            </div>

            {/* Permissions Section */}
            <div className="bg-white p-5 shadow-md">
              <div className="flex items-center gap-3 mb-5 pb-3 border-b-2 border-gray-100">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#317D95] to-[#348299] flex items-center justify-center shadow-md">
                  <Shield className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Permissions & Access</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Define what actions this API key can perform</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-start space-x-3 p-4 bg-gradient-to-r from-green-50/50 to-emerald-50/50 rounded-xl border border-green-200/60 hover:border-green-300 hover:shadow-sm transition-all">
                  <Checkbox 
                    id="read" 
                    defaultChecked 
                    className="mt-1 data-[state=checked]:bg-[#307A91] data-[state=checked]:border-[#307A91]" 
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Eye className="h-4 w-4 text-[#307A91]" />
                      <Label htmlFor="read" className="text-sm font-semibold text-gray-900 cursor-pointer">
                        Read Access
                      </Label>
                    </div>
                    <p className="text-xs text-gray-600">View and retrieve data from the API endpoints</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-4 bg-gradient-to-r from-blue-50/50 to-cyan-50/50 rounded-xl border border-blue-200/60 hover:border-blue-300 hover:shadow-sm transition-all">
                  <Checkbox 
                    id="write" 
                    defaultChecked 
                    className="mt-1 data-[state=checked]:bg-[#348299] data-[state=checked]:border-[#348299]" 
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Edit className="h-4 w-4 text-[#348299]" />
                      <Label htmlFor="write" className="text-sm font-semibold text-gray-900 cursor-pointer">
                        Write Access
                      </Label>
                    </div>
                    <p className="text-xs text-gray-600">Create and update data via the API</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-4 bg-gradient-to-r from-red-50/50 to-orange-50/50 rounded-xl border border-red-200/60 hover:border-red-300 hover:shadow-sm transition-all">
                  <Checkbox 
                    id="delete" 
                    className="mt-1 data-[state=checked]:bg-[#FE7F50] data-[state=checked]:border-[#FE7F50]" 
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Trash2 className="h-4 w-4 text-[#FE7F50]" />
                      <Label htmlFor="delete" className="text-sm font-semibold text-gray-900 cursor-pointer">
                        Delete Access
                      </Label>
                    </div>
                    <p className="text-xs text-gray-600">Remove data permanently from the system</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer - Fixed at bottom */}
          <div className="px-8 py-6 bg-gradient-to-r from-gray-100 to-gray-50 border-t-2 border-gray-200 flex justify-between items-center sticky bottom-0">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <AlertTriangle className="h-4 w-4 text-[#FE7F50]" />
              <span>All fields marked with <span className="text-[#FE7F50] font-semibold">*</span> are required</span>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="px-8 h-11 border-2 border-gray-300 hover:bg-white hover:border-gray-400 font-medium transition-all"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveNewKey}
                className="px-8 h-11 bg-gradient-to-r from-[#307A91] to-[#348299] hover:from-[#348299] hover:to-[#317D95] text-white font-semibold shadow-lg shadow-[#307A91]/30 hover:shadow-xl hover:shadow-[#307A91]/40 transition-all"
              >
                <Key className="h-4 w-4 mr-2" />
                Create API Key
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit API Key Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)}>
        <div className="max-w-3xl -m-6">
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-8 py-6 rounded-t-lg relative">
            <button
              onClick={() => setShowEditModal(false)}
              className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              aria-label="Close"
            >
              <XCircle className="h-6 w-6" />
            </button>
            <div className="flex items-center space-x-3 pr-12">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                <Edit className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Edit API Key</h2>
                <p className="text-purple-100 text-sm mt-1">Update API key configuration and settings</p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-6 max-h-[70vh] overflow-y-auto bg-gray-50">
            {/* Basic Information Section */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Info className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Basic Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Key Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Production API Key"
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Application <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.application}
                    onChange={(e) => setFormData({...formData, application: e.target.value})}
                    placeholder="e.g., Mobile App, Web App"
                    className="h-10"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm font-medium text-gray-700">Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe the purpose and functionality of this API key..."
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Environment <span className="text-red-500">*</span>
                  </Label>
                  <Select value={formData.environment} onValueChange={(value) => setFormData({...formData, environment: value})}>
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select environment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="production">Production</SelectItem>
                      <SelectItem value="staging">Staging</SelectItem>
                      <SelectItem value="development">Development</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Rate Limiting Section */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                  <Target className="h-5 w-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Rate Limiting</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Rate Limit <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="number"
                    value={formData.rateLimit}
                    onChange={(e) => setFormData({...formData, rateLimit: parseInt(e.target.value)})}
                    placeholder="1000"
                    className="h-10"
                    min="1"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Period <span className="text-red-500">*</span>
                  </Label>
                  <Select value={formData.rateLimitPeriod} onValueChange={(value) => setFormData({...formData, rateLimitPeriod: value})}>
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minute">Per Minute</SelectItem>
                      <SelectItem value="hour">Per Hour</SelectItem>
                      <SelectItem value="day">Per Day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Permissions Section */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Permissions</h3>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border">
                  <Checkbox id="edit-read" defaultChecked />
                  <Label htmlFor="edit-read" className="text-sm font-medium text-gray-700 cursor-pointer flex-1">
                    Read Access
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border">
                  <Checkbox id="edit-write" defaultChecked />
                  <Label htmlFor="edit-write" className="text-sm font-medium text-gray-700 cursor-pointer flex-1">
                    Write Access
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border">
                  <Checkbox id="edit-delete" />
                  <Label htmlFor="edit-delete" className="text-sm font-medium text-gray-700 cursor-pointer flex-1">
                    Delete Access
                  </Label>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="px-8 py-5 bg-white rounded-b-lg border-t flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowEditModal(false)}
              className="px-6 h-10 hover:bg-gray-100"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEditKey}
              className="px-6 h-10 bg-purple-600 hover:bg-purple-700 text-white"
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      {/* Metric Detail Modal */}
      {selectedMetric && (
        <Modal isOpen={showMetricModal} onClose={() => setShowMetricModal(false)}>
          <div className="max-w-2xl -m-6">
            <div 
              className="px-8 py-6 rounded-t-lg relative"
              style={{ 
                background: `linear-gradient(135deg, ${selectedMetric.bgColor} 0%, ${selectedMetric.bgColor}dd 100%)`,
                color: 'white'
              }}
            >
              <button
                onClick={() => setShowMetricModal(false)}
                className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
                aria-label="Close"
              >
                <XCircle className="h-6 w-6" />
              </button>
              <div className="flex items-center justify-between mb-4 pr-12">
                <h2 className="text-2xl font-bold">{selectedMetric.title}</h2>
                {React.createElement(selectedMetric.icon, { className: 'h-10 w-10' })}
              </div>
              <div className="text-4xl font-bold mb-2">
                {selectedMetric.value.toLocaleString ? selectedMetric.value.toLocaleString() : selectedMetric.value}
              </div>
              <div className="flex items-center text-lg" style={{ opacity: 0.9 }}>
                {selectedMetric.trendDirection === 'up' ? (
                  <ArrowUp className="h-5 w-5 mr-2" />
                ) : (
                  <ArrowDown className="h-5 w-5 mr-2" />
                )}
                <span>{selectedMetric.trend} from yesterday</span>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-gray-700 text-lg">{selectedMetric.details.description}</p>
              
              <div className="grid grid-cols-2 gap-4 mt-6">
                {Object.entries(selectedMetric.details).map(([key, value]) => {
                  if (key === 'description') return null;
                  return (
                    <div key={key} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-sm text-gray-600 capitalize mb-1">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </div>
                      <div className="text-2xl font-bold" style={{ color: selectedMetric.bgColor }}>
                        {value.toLocaleString ? value.toLocaleString() : value}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end mt-6 space-x-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowMetricModal(false)}
                  className="hover:bg-gray-100 transition-all"
                  style={{
                    borderColor: selectedMetric.bgColor,
                    color: selectedMetric.bgColor
                  }}
                >
                  Close
                </Button>
                <Button 
                  onClick={() => {
                    setShowMetricModal(false);
                    handleUsageAnalytics();
                  }}
                  className="transition-all hover:scale-105"
                  style={{
                    background: `linear-gradient(135deg, ${selectedMetric.bgColor} 0%, ${selectedMetric.bgColor}dd 100%)`,
                    color: 'white',
                    border: 'none'
                  }}
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  View Analytics
                </Button>
                <Button 
                  onClick={() => {
                    setShowMetricModal(false);
                    handleSecuritySettings();
                  }}
                  className="transition-all hover:scale-105"
                  style={{
                    background: 'linear-gradient(135deg, #348299 0%, #317D95 100%)',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Security Settings
                </Button>
                <Button 
                  onClick={() => {
                    setShowMetricModal(false);
                    handleExportKeys();
                  }}
                  className="transition-all hover:scale-105"
                  style={{
                    background: 'linear-gradient(135deg, #FE7F50 0%, #FF7F4C 100%)',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Data
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default APIKeyManagement;
